/**
 * Lo que el tablero de Inicio dice del cobro y del estado de los documentos.
 *
 * Todo sale de la base local: Inicio es la pantalla que más se abre y tiene que
 * contestar sin conexión, así que aquí no se pregunta nada a Business Central.
 * Las funciones son puras --reciben las filas y devuelven cifras-- para poder
 * probarlas sin base.
 */
import dayjs from "dayjs";
import { addMoney, moneyEquals, roundMoney, sumMoney } from "../utils/money";
import { getDocumentTypeSign } from "../constants/documentType";
import { ESTADO_DEL_TIMBRADO, timbradoDeLaFila } from "./estadoDelTimbrado";
import { estaEnBusinessCentral } from "../components/Invoice/queSePuedeHacer";
import { paisDistingueTiquete } from "../components/Invoice/tipoDeComprobante";

const texto = (valor) => String(valor ?? "").trim();

/** Cómo se nombra un documento en una lista: su número, o el id local si aún no tiene. */
export const numeroDelDocumento = (doc) =>
  texto(doc?.No) || texto(doc?.Pre_Assigned_No) || (doc?.id != null ? `#${doc.id}` : "—");

/** Cuándo se hizo el documento, con la hora de la venta si la tiene. */
export const momentoDelDocumento = (doc) => {
  const fecha = texto(doc?.Posting_Date).slice(0, 10);
  const hora = texto(doc?.Hora_creacion);
  if (fecha && hora) {
    const conHora = dayjs(`${fecha}T${hora}`);
    if (conHora.isValid()) return conHora;
  }
  const modificado = dayjs(doc?.lastModified);
  if (doc?.lastModified && modificado.isValid()) return modificado;
  return fecha ? dayjs(fecha) : null;
};

/**
 * Por qué un documento no cuadra su total con lo cobrado o devuelto. Se nombran
 * desde el lado de quien lo tiene que revisar: una nota de crédito «sin
 * devolución» es una a la que nadie le registró cómo se devolvió el dinero.
 */
export const MOTIVOS_DEL_DESCUADRE = {
  facturaSinCobro: { singular: "factura sin cobro", plural: "facturas sin cobro" },
  facturaConCobroIncompleto: {
    singular: "factura con cobro incompleto",
    plural: "facturas con cobro incompleto",
  },
  facturaCobradaDeMas: { singular: "factura cobrada de más", plural: "facturas cobradas de más" },
  abonoSinDevolucion: {
    singular: "nota de crédito sin devolución",
    plural: "notas de crédito sin devolución",
  },
  abonoConDevolucionIncompleta: {
    singular: "nota de crédito con devolución incompleta",
    plural: "notas de crédito con devolución incompleta",
  },
  abonoQueDevolvioDeMas: {
    singular: "nota de crédito que devolvió de más",
    plural: "notas de crédito que devolvieron de más",
  },
};

const motivoDelDescuadre = (esAbono, pagado, total) => {
  if (roundMoney(pagado, 2) === 0) return esAbono ? "abonoSinDevolucion" : "facturaSinCobro";
  if (pagado < total) return esAbono ? "abonoConDevolucionIncompleta" : "facturaConCobroIncompleto";
  return esAbono ? "abonoQueDevolvioDeMas" : "facturaCobradaDeMas";
};

/**
 * La causa de la diferencia, en un renglón: los documentos que no cuadran,
 * agrupados por motivo y con el motivo que más pesa primero.
 *
 * «1 nota de crédito sin devolución: VNR15-000309 · 2 facturas con cobro
 * incompleto: VF-1, VF-2». No lleva importes: la cifra de la diferencia la pone
 * la pantalla, con el formato de moneda del país.
 *
 * @param {Array<{numero: string, motivo: string, efecto: number}>} documentos
 * @param {number} [maxNumeros=3] Cuántos números enseñar por motivo antes de resumir.
 */
export const textoDeLaCausa = (documentos = [], maxNumeros = 3) => {
  const grupos = new Map();
  documentos.forEach((doc) => {
    if (!grupos.has(doc.motivo)) grupos.set(doc.motivo, { numeros: [], peso: 0 });
    const grupo = grupos.get(doc.motivo);
    grupo.numeros.push(doc.numero);
    grupo.peso += Math.abs(doc.efecto);
  });

  return [...grupos.entries()]
    .sort(([, a], [, b]) => b.peso - a.peso)
    .map(([motivo, { numeros }]) => {
      const nombre = MOTIVOS_DEL_DESCUADRE[motivo];
      const cuantos = numeros.length;
      const visibles = numeros.slice(0, maxNumeros).join(", ");
      const resto = cuantos > maxNumeros ? ` y ${cuantos - maxNumeros} más` : "";
      return `${cuantos} ${cuantos === 1 ? nombre.singular : nombre.plural}: ${visibles}${resto}`;
    })
    .join(" · ");
};

/**
 * Ventas por forma de pago, **netas**: lo cobrado en facturas menos lo devuelto
 * en notas de crédito. Decisión de Solme del 17/09/2026.
 *
 * Es la misma cuenta que hace el arqueo del cierre, que netea los tipos de
 * documento dentro de cada forma de pago. Antes el panel sumaba sólo facturas,
 * y su total no se podía comparar con Ventas netas, que sí resta los abonos.
 *
 * **Las líneas deducidas no cuentan**, igual que en el arqueo: se crean para que
 * una nota de crédito sobre una factura histórica sin detalle de pago tenga forma
 * de pago, pero no movieron dinero. Por eso el total puede no coincidir con
 * Ventas netas. Cuando no coincide se devuelve la diferencia y **qué documentos
 * la explican**, en vez de dejar dos cifras distintas sin razón a la vista.
 *
 * @param {object} p
 * @param {Array<{invoice: object, total: number}>} p.documentos Facturas y notas de crédito del período, con su total.
 * @param {(invoice: object) => Array} p.pagosDe Las líneas de pago de un documento.
 * @param {Array} p.formasDePago El catálogo, para el nombre de cada forma.
 * @param {number} p.ventasNetas Para comparar el total.
 * @param {number} p.decimals Decimales de cálculo del país.
 * @param {string} p.countryCode Para el signo de cada tipo de documento.
 */
export const formasDePagoNetas = ({
  documentos = [],
  pagosDe,
  formasDePago = [],
  ventasNetas = 0,
  decimals = 2,
  countryCode,
}) => {
  const vistas = new Set();
  const porForma = new Map();
  const porDocumento = [];

  documentos.forEach((documento) => {
    const { invoice } = documento;
    const signo = getDocumentTypeSign(invoice?.Document_Type, countryCode) < 0 ? -1 : 1;
    let pagado = 0;

    (pagosDe(invoice) || []).forEach((linea) => {
      if (!linea || linea.deleted || linea.deducida) return;
      // Una misma línea entra por sus dos claves --id local y número--: una vez.
      if (linea.id != null) {
        if (vistas.has(linea.id)) return;
        vistas.add(linea.id);
      }
      const codigo = texto(linea.Payment_Method).toUpperCase();
      const forma = formasDePago.find((fp) => texto(fp.Code).toUpperCase() === codigo);
      const nombre = forma?.Description || linea.Payment_Method || "Otro";
      const importe = Math.abs(Number(linea.Amount) || 0);
      pagado = addMoney(pagado, importe, decimals);
      porForma.set(nombre, addMoney(porForma.get(nombre) || 0, signo * importe, decimals));
    });

    if (Number.isFinite(documento.total)) {
      porDocumento.push({ invoice, signo, total: documento.total, pagado });
    }
  });

  // Una forma que vendió y devolvió lo mismo no movió nada neto: fuera.
  const netas = Array.from(porForma.entries()).filter(([, importe]) => roundMoney(importe, 2) !== 0);
  const cobradoNeto = sumMoney(netas, ([, importe]) => importe, decimals);

  // Una forma con neto negativo --devolvió más de lo que cobró-- no puede ser
  // una porción del círculo: se lista con su importe y 0 %. Los porcentajes se
  // reparten entre las que sumaron.
  const positivo = sumMoney(
    netas.filter(([, importe]) => importe > 0),
    ([, importe]) => importe,
    decimals
  );

  const ventasPorFormaDePago = netas
    .map(([label, amount]) => ({
      label,
      amount,
      percent: amount > 0 && positivo > 0 ? (amount / positivo) * 100 : 0,
    }))
    .sort((a, b) => b.amount - a.amount);

  // Un céntimo por documento es residuo de redondeo, no un descuadre. Ojo:
  // `moneyEquals` mide la tolerancia en unidades menores --céntimos con dos
  // decimales--, no en moneda.
  const toleranciaEnCentimos = Math.max(1, documentos.length);
  const diferenciaConVentasNetas = moneyEquals(cobradoNeto, ventasNetas, toleranciaEnCentimos, 2)
    ? 0
    : roundMoney(cobradoNeto - ventasNetas, decimals);

  // Documento por documento, con un céntimo de tolerancia cada uno. `efecto` es
  // lo que ese documento aporta a «cobrado neto menos ventas netas»: una nota de
  // crédito sin devolución de 22.170 aporta +22.170, porque resta en las ventas
  // y no resta en ninguna forma de pago.
  const documentosQueNoCuadran = porDocumento
    .filter((doc) => !moneyEquals(doc.pagado, doc.total, 1, 2))
    .map((doc) => ({
      numero: numeroDelDocumento(doc.invoice),
      esAbono: doc.signo < 0,
      total: roundMoney(doc.total, 2),
      pagado: roundMoney(doc.pagado, 2),
      efecto: roundMoney(doc.signo * (doc.pagado - doc.total), 2),
      motivo: motivoDelDescuadre(doc.signo < 0, doc.pagado, doc.total),
    }))
    .sort((a, b) => Math.abs(b.efecto) - Math.abs(a.efecto));

  return {
    ventasPorFormaDePago,
    cobradoNeto,
    diferenciaConVentasNetas,
    documentosQueNoCuadran,
    // Sólo cuando la diferencia se ve: un documento de +1 céntimo y otro de −1
    // no son una causa que haya que explicar.
    causaDeLaDiferencia: diferenciaConVentasNetas ? textoDeLaCausa(documentosQueNoCuadran) : "",
  };
};

const grupo = (lista, motivoDe) => ({
  cuantos: lista.length,
  recientes: lista
    .slice()
    .sort(
      (a, b) => (momentoDelDocumento(b)?.valueOf() || 0) - (momentoDelDocumento(a)?.valueOf() || 0)
    )
    .slice(0, 3)
    .map((doc) => ({ numero: numeroDelDocumento(doc), motivo: motivoDe(doc) })),
});

/**
 * El camino de los documentos del período, y los que necesitan a una persona.
 *
 * **El timbrado sólo se lee de la fila**, y la fila sólo lo sabe si alguien
 * preguntó: se refresca al actualizar correlativos y después de Traer
 * Históricos. Un documento contabilizado del que nunca se preguntó cuenta como
 * **sin consultar**, no como sin timbrar: afirmar lo segundo sería un aviso
 * falso sobre un comprobante que puede estar bien.
 *
 * @param {object} p
 * @param {Array<object>} p.documentos Filas del período, sin borradores ni eliminadas.
 * @param {string} p.countryCode Sólo los países con comprobante que timbrar llevan ese paso.
 */
export const resumenDeDocumentos = ({ documentos = [], countryCode }) => {
  const timbra = paisDistingueTiquete(countryCode);
  const timbrados = timbra ? documentos.map((doc) => [doc, timbradoDeLaFila(doc)]) : [];
  const conEstado = (estado) => timbrados.filter(([, t]) => t?.estado === estado).map(([doc]) => doc);

  const contabilizadas = documentos.filter((doc) => doc.posted);

  return {
    embudo: {
      emitidas: documentos.length,
      enBusinessCentral: documentos.filter(estaEnBusinessCentral).length,
      contabilizadas: contabilizadas.length,
      timbradas: timbra
        ? contabilizadas.filter((doc) => timbradoDeLaFila(doc)?.estado === ESTADO_DEL_TIMBRADO.TIMBRADO)
            .length
        : null,
      sinConsultar: timbra
        ? contabilizadas.filter((doc) => timbradoDeLaFila(doc) === null).length
        : null,
    },
    atencion: {
      rechazadasPorHacienda: timbra
        ? grupo(
            conEstado(ESTADO_DEL_TIMBRADO.RECHAZADO),
            (doc) => texto(doc.MensajeElectronico) || texto(doc.Mensaje)
          )
        : null,
      noTimbraran: timbra
        ? grupo(conEstado(ESTADO_DEL_TIMBRADO.NO_TIMBRARA), (doc) => texto(doc.Mensaje))
        : null,
      // Con error y ya no pendientes: nadie las vuelve a mandar. Son las que
      // necesitan que alguien corrija un dato.
      frenadas: grupo(
        documentos.filter((doc) => doc.syncError && !doc.pendingSync),
        (doc) => texto(doc.syncErrorMessage)
      ),
      // Con error y todavía pendientes: la sincronización lo vuelve a intentar.
      esperandoReintento: grupo(
        documentos.filter((doc) => doc.syncError && doc.pendingSync),
        (doc) => texto(doc.syncErrorMessage)
      ),
    },
  };
};

/**
 * Lo que está sin subir a Business Central, **sin mirar el período**: una venta
 * de hace tres días sin enviar importa aunque el tablero esté en «Hoy».
 *
 * @param {Array<object>} pendientes Filas con `pendingSync`, sin borradores ni eliminadas.
 */
export const loMasViejoSinEnviar = (pendientes = []) => {
  if (!pendientes.length) return { cuantos: 0, conError: 0, masViejo: null };

  const conMomento = pendientes
    .map((doc) => [doc, momentoDelDocumento(doc)])
    .filter(([, momento]) => momento)
    .sort(([, a], [, b]) => a.valueOf() - b.valueOf());

  const [doc, momento] = conMomento[0] || [pendientes[0], null];

  return {
    cuantos: pendientes.length,
    conError: pendientes.filter((p) => p.syncError).length,
    masViejo: { numero: numeroDelDocumento(doc), desde: momento ? momento.toISOString() : null },
  };
};

/** «hace 5 min», «hace 3 h», «hace 2 días». */
export const haceCuanto = (desde, ahora = dayjs()) => {
  if (!desde) return "";
  const inicio = dayjs(desde);
  if (!inicio.isValid()) return "";
  const minutos = Math.max(0, dayjs(ahora).diff(inicio, "minute"));
  if (minutos < 60) return `hace ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `hace ${horas} h`;
  const dias = Math.floor(horas / 24);
  return `hace ${dias} ${dias === 1 ? "día" : "días"}`;
};
